"use client";

/**
 * JobList — renders the grid of job cards with loading and empty states.
 */
import type { AnalysisJob } from "../types";
import { JobCard } from "./JobCard";

interface Props {
  jobs: AnalysisJob[];
  isLoading: boolean;
  onDelete: (jobId: string) => Promise<void>;
  onRename: (jobId: string, title: string) => Promise<void>;
}

export function JobList({ jobs, isLoading, onDelete, onRename }: Props) {
  if (isLoading) {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
        {[1, 2, 3].map((n) => (
          <div key={n} style={{
            height: 200, borderRadius: 14,
            border: "1px solid var(--line-soft)",
            background: "linear-gradient(90deg, rgba(128,128,128,0.08) 25%, rgba(128,128,128,0.18) 50%, rgba(128,128,128,0.08) 75%)",
            backgroundSize: "800px 100%",
            animation: "shimmer 1.4s linear infinite",
          }} />
        ))}
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "48px 0", color: "var(--ink-soft)" }}>
        <div style={{ marginBottom: 16, display: "flex", justifyContent: "center" }}>
          <svg width="54" height="54" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ opacity: 0.3 }}>
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
            <line x1="8" y1="13" x2="10" y2="13"/><line x1="8" y1="17" x2="10" y2="17"/>
            <line x1="14" y1="13" x2="16" y2="13"/><line x1="14" y1="17" x2="16" y2="17"/>
          </svg>
        </div>
        <p style={{ margin: 0, fontSize: 14 }}>No recent analyses yet. Upload a file to get started!</p>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16 }}>
      {jobs.map((job) => (
        <JobCard key={job.id} job={job} onDelete={onDelete} onRename={onRename} />
      ))}
    </div>
  );
}
